import { ArrowLeftIcon, GearIcon, PlusIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'

import ChecklistsAssignTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsAssignTaskModal/ChecklistsAssignTaskModal'
import ChecklistsCreateNewTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsCreateNewTaskModal/ChecklistsCreateNewTaskModal'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, routerFactory } from '@/factories'
import { useCaptureEvent } from '@/hooks'
import { useLocalStorage } from '@/hooks/use-local-storage'
import {
  getSelectedChecklistTemplate,
  setSelectedChecklistTemplate,
} from '@/reducers/Organisation/checklistsReducer'
import { useAppDispatch } from '@/store/hooks'

import { CHECKLISTS_UI_VERSION_KEY } from './checklists.page.constants'
import { ChecklistsPageOutletContext, UiVersion } from './checklists.page.types'

export const ChecklistsPage = () => {
  const { t } = useTranslation(['checklists', 'common'])
  const { captureEvent } = useCaptureEvent()
  const location = useLocation()

  // Note: UI version defining which table is used - legacy or new
  const [uiVersion, setUiVersion] = useLocalStorage<UiVersion>(CHECKLISTS_UI_VERSION_KEY, 'new')

  useEffect(() => {
    captureEvent('$pageview', {
      page_title: 'Checklists',
      ui_version: uiVersion,
    })

    return () => {
      captureEvent('$pageleave', {
        page_title: 'Checklists',
        ui_version: uiVersion,
      })
    }
  }, [uiVersion])

  const handleUIToggle = () => {
    const nextVersion: UiVersion = uiVersion === 'new' ? 'legacy' : 'new'

    captureEvent('user_action', {
      from_path: location.pathname,
      from_ui_version: uiVersion,
      name: 'design_toggle',
      screen: 'Checklists',
      to_path: location.pathname,
      to_ui_version: nextVersion,
    })

    setUiVersion(nextVersion)
  }

  // Note: Old redux used here to handle checklist template preview/edit
  const selectedChecklistTemplate = useSelector((state) => getSelectedChecklistTemplate(state).data)
  const dispatch = useAppDispatch()

  const { useMatch } = routerFactory.useRouter()
  const isDashboardPath = useMatch({
    path: '/dashboard/building-dashboard/checklists',
  })
  const isSettingsPath = useMatch({
    path: '/dashboard/building-dashboard/checklists/settings',
  })

  const isInspectionPath = useMatch({
    path: '/dashboard/building-dashboard/checklists/inspection/:inspectionId',
  })

  // Note: Old modal is used here - it will be replaced with new one in the future
  const handleCreateNewTask = () => {
    popupFactory.open(ChecklistsCreateNewTaskModal, {})
  }

  // Note: Old modal is used here - it will be replaced with new one in the future
  const handleAssignTask = () => {
    popupFactory.open(ChecklistsAssignTaskModal, {
      showCreateNewTask: handleCreateNewTask,
    })
  }

  return (
    <Page>
      {isDashboardPath && (
        <PageHeader
          title={t('checklists:root.title')}
          actions={
            <>
              <ToggleSwitch
                falsyLabel={t('checklists:root.designToggle.checklists')}
                truthyLabel={t('checklists:root.designToggle.allTasks')}
                checked={uiVersion === 'new'}
                onChange={handleUIToggle}
              />
              <Link
                size="small"
                use="secondary"
                to={{
                  path: '/dashboard/building-dashboard/checklists/settings',
                }}
              >
                <Icon icon={GearIcon} />
                {t('checklists:root.settings')}
              </Link>
              <Button size="small" onClick={handleAssignTask}>
                {t('checklists:root.assignTask')}
                <Icon icon={PlusIcon} />
              </Button>
            </>
          }
        />
      )}
      {isSettingsPath && (
        <PageHeader
          title={t('checklists:root.title')}
          backButton={
            // Note: In settings user can select template preview/edit.
            // It's managed by redux state, so if the template is selected - redux state get's cleared and user is moved to settings again.
            selectedChecklistTemplate ? (
              <Button
                use="secondary"
                size="small"
                onClick={() => dispatch(setSelectedChecklistTemplate({ data: null, mode: null }))}
              >
                <Icon icon={ArrowLeftIcon} />
                {t('common:actions.back')}
              </Button>
            ) : (
              <Link
                navigateBack
                use="secondary"
                size="small"
                to={{
                  path: '/dashboard/building-dashboard/checklists',
                }}
              >
                <Icon icon={ArrowLeftIcon} />
                {t('common:actions.back')}
              </Link>
            )
          }
          actions={
            <Button size="small" onClick={handleCreateNewTask}>
              <Icon icon={PlusIcon} />
              {t('checklists:root.createNewTask')}
            </Button>
          }
        />
      )}
      {isInspectionPath && (
        <PageHeader
          title={t('checklists:root.inspectionTitle')}
          backButton={
            <Link
              navigateBack
              use="secondary"
              size="small"
              to={{
                path: '/dashboard/building-dashboard/checklists',
              }}
            >
              <Icon icon={ArrowLeftIcon} />
              {t('common:actions.back')}
            </Link>
          }
        />
      )}
      <Outlet context={{ uiVersion } as ChecklistsPageOutletContext} />
    </Page>
  )
}
